import { useState, useEffect, useMemo } from 'react';
import { ComplianceRules, getViolationCategory, checkGreenPatterns } from './emailComplianceRules';

const stripPlaceholders = (text) =>
  text.replace(/\{\{[^}]*\}\}|\{[^}]*\}/g, '');

const EMPTY_STATS = {
  totalViolations: 0,
  highRisk: 0,
  mediumRisk: 0,
  spamRisk: 0,
  hasGreenPatterns: false,
  greenPatternCount: 0,
};

/** Synchronous scan. The list badges use this so they don't pop in a beat later. */
export const analyzeEmailCompliance = (emailText) => {
  if (!emailText || emailText.trim().length === 0) {
    return {
      violations: [],
      spamWarnings: [],
      greenPatterns: [],
      complianceStatus: getViolationCategory([]),
      stats: EMPTY_STATS,
    };
  }

  const cleaned = stripPlaceholders(emailText);
  const seen = new Set();
  const violations = [
    ...scanTriggers(cleaned, ComplianceRules.RED_TRIGGERS, 'RED', seen),
    ...scanTriggers(cleaned, ComplianceRules.YELLOW_TRIGGERS, 'YELLOW', seen),
  ];
  const spamSeen = new Set();
  const spamWarnings = scanTriggers(cleaned, ComplianceRules.SPAM_TRIGGERS, 'SPAM', spamSeen);
  const greenPatterns = checkGreenPatterns(cleaned);

  return {
    violations,
    spamWarnings,
    greenPatterns,
    complianceStatus: getViolationCategory(violations),
    stats: {
      totalViolations: violations.length,
      highRisk: violations.filter((v) => v.severity === 'high').length,
      mediumRisk: violations.filter((v) => v.severity === 'medium').length,
      spamRisk: spamWarnings.length,
      hasGreenPatterns: greenPatterns.length > 0,
      greenPatternCount: greenPatterns.length,
    },
  };
};

const scanTriggers = (text, triggers, type, seen) => {
  const results = [];
  triggers.forEach((trigger) => {
    const matches = [...text.matchAll(trigger.pattern)];
    matches.forEach((match) => {
      const key = `${trigger.label}::${match[0].toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      results.push({
        type,
        severity: trigger.severity,
        label: trigger.label,
        reason: trigger.reason,
        matchedText: match[0],
        position: match.index
      });
    });
  });
  return results;
};

export const useEmailComplianceChecker = (emailText) => {
  const [violations, setViolations] = useState([]);
  const [spamWarnings, setSpamWarnings] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (!emailText || emailText.trim().length === 0) {
      setViolations([]);
      setSpamWarnings([]);
      return;
    }

    setIsAnalyzing(true);

    const timer = setTimeout(() => {
      const result = analyzeEmailCompliance(emailText);
      setViolations(result.violations);
      setSpamWarnings(result.spamWarnings);
      setIsAnalyzing(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [emailText]);

  const complianceStatus = useMemo(() => {
    return getViolationCategory(violations);
  }, [violations]);

  const greenPatterns = useMemo(() => {
    if (!emailText) return [];
    return checkGreenPatterns(stripPlaceholders(emailText));
  }, [emailText]);

  const stats = useMemo(() => {
    const redCount = violations.filter(v => v.severity === 'high').length;
    const yellowCount = violations.filter(v => v.severity === 'medium').length;
    const spamCount = spamWarnings.length;

    return {
      totalViolations: violations.length,
      highRisk: redCount,
      mediumRisk: yellowCount,
      spamRisk: spamCount,
      hasGreenPatterns: greenPatterns.length > 0,
      greenPatternCount: greenPatterns.length
    };
  }, [violations, spamWarnings, greenPatterns]);

  const recommendations = useMemo(() => {
    const allClean = violations.length === 0 && spamWarnings.length === 0;

    if (allClean) {
      if (greenPatterns.length > 0) {
        return ['✅ Well done! You are using neutral, legally safe wording.'];
      }
      return ['✅ No problematic wording found.'];
    }

    const recs = [];
    const redViolations = violations.filter(v => v.severity === 'high');
    const yellowViolations = violations.filter(v => v.severity === 'medium');

    if (redViolations.length > 0) {
      recs.push('⚠️ Remove all price/value statements, scarcity tactics, and call-to-actions.');
      recs.push('💡 Replace sales language with neutral questions.');
    }

    if (yellowViolations.length > 0) {
      recs.push('⚠️ Avoid combinations of "free/gratis" with service descriptions.');
      recs.push('💡 Use instead: "Would a brief exchange be interesting for you?"');
    }

    if (spamWarnings.length > 0) {
      recs.push('📧 Remove spam trigger words to improve inbox placement.');
      if (spamWarnings.some(s => s.label === 'ALL CAPS Word' || s.label === 'Excessive Exclamation Marks')) {
        recs.push('📧 Use normal capitalization and limit punctuation to single marks.');
      }
    }

    if (greenPatterns.length === 0) {
      recs.push('💡 Tip: Ask open questions without service promises.');
    }

    return recs;
  }, [violations, spamWarnings, greenPatterns]);

  return {
    violations,
    spamWarnings,
    complianceStatus,
    stats,
    recommendations,
    isAnalyzing,
    greenPatterns
  };
};

